import React from 'react';
import PropTypes from 'prop-types';

const jobIconPropertyTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  title: PropTypes.string.isRequired,
  dPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
  fills: PropTypes.arrayOf(PropTypes.string),
  fill: PropTypes.string,
};

interface JobIconProperties {
  width?: number;
  height?: number;
  title: string;
  dPaths: string[];
  fills?: (string | undefined)[];
  fill?: string;
}

const JobIcon = ({
  width, height, dPaths, title, fills, fill, ...rest
}: JobIconProperties) => (
  <svg
    viewBox="0 0 24 24"
    width={width ?? 24}
    height={height ?? 24}
    {...rest}
  >
    <title>{title}</title>
    {dPaths.map((dPath, index) => (
      <path
        d={dPath}
        fill={fills?.[index] ?? fill}
        key={dPath}
      />
    ))}
  </svg>
);

JobIcon.propTypes = jobIconPropertyTypes;

interface JobSpecificIconProperties extends
  Omit<JobIconProperties, 'dPaths' | 'fills' | 'height' | 'title' | 'width'>,
  React.SVGProps<SVGSVGElement> {
  width?: number;
  height?: number;
  title?: string;
}

const JobTitleIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Title'}
    {...rest}
  />
);

const JobCompanyIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Company'}
    {...rest}
  />
);

const JobLocationIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Location'}
    {...rest}
  />
);

const JobDatePostedIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Date Posted'}
    {...rest}
  />
);

const JobCountryIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Country'}
    {...rest}
  />
);

const JobCategoryIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job Category'}
    {...rest}
  />
);

const JobHowToApplyIcon = ({
  title, ...rest
}: JobSpecificIconProperties) => (
  <JobIcon
    dPaths={[
      // eslint-disable-next-line max-len
      'M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z',
      'M0 0h24v24H0z',
    ]}
    fills={[
      rest.fill ?? undefined, 'none',
    ]}
    title={title ?? 'Job How To Apply'}
    {...rest}
  />
);

export {
  JobTitleIcon,
  JobCompanyIcon,
  JobLocationIcon,
  JobDatePostedIcon,
  JobCountryIcon,
  JobCategoryIcon,
  JobHowToApplyIcon,
};
